import { AxiosError } from 'axios'
import moment from 'moment'
import { createStandardAction } from 'typesafe-actions'
import { AnyAction } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'

import { USERS_URL } from '../../constants/urls/backend'
import { IUser, INewUser } from '../../types/user'
import { IRequestResult } from '../types'
import { apiRoute } from '../../utils/routeUtils'
import { axiosWithAdal } from '../../utils/http'

const usersSuccess = createStandardAction('@@users/SUCCESS')<IUser[]>()
const usersError = createStandardAction('@@users/ERROR')<string[]>()
const usersRequest = createStandardAction('@@users/REQUEST')()

const userCreateSuccess = createStandardAction('@@users/CREATE_SUCCCESS')<INewUser>()

const userUpdateSuccess = createStandardAction('@@users/UPDATE_SUCCESS')<IUser>()
const userUpdateError = createStandardAction('@@users/UPDATE_ERROR')<string[]>()
const userUpdateRequest = createStandardAction('@@users/UPDATE_REQUEST')()

interface IResponse {
  id: number
  email: string
  updated: string
  installation: number
  created: string
  last_login: string
}

const fetchUsers = (): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
    dispatch(usersRequest())
    try {
      const response = await axiosWithAdal.get<IResponse[]>(apiRoute(USERS_URL), {
        headers: {
          'Content-Type': 'application/json',
        }
      })

      const payload: IUser[] = response.data.map(x => {
        return {
          id: x.id,
          email: x.email,
          created: moment(x.created),
          lastLogin: moment(x.last_login),
          updated: moment(x.updated),
          installation: x.installation,
        }
      })
      dispatch(usersSuccess(payload))
    } catch (e) {
      dispatch(usersError([ e.message ]))
      return
    }
  }
}

const createUser = (
  user: INewUser
): ThunkAction<Promise<IRequestResult>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<IRequestResult> => {
    try {
      const response = await axiosWithAdal.post<IResponse>(
        apiRoute(USERS_URL),
        JSON.stringify(user), {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      dispatch(userCreateSuccess({
        email: response.data.email,
        installation: response.data.installation,
      }))

      return {
        status: response.status,
        message: response.statusText,
      }
    } catch (e) {
      const error = e as AxiosError
      return {
        status: error.code ? parseInt(error.code, 10) : 500,
        message: error.message
      }
    }
  }
}

const updateUser = (user: IUser): ThunkAction<Promise<IRequestResult>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<IRequestResult> => {
    dispatch(userUpdateRequest())
    try {
      const { id, email, ...requestBody } = user
      const response = await axiosWithAdal.put<IResponse>(
        `${apiRoute(USERS_URL)}${user.id}`, JSON.stringify(requestBody), {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const payload: IUser = {
        id: response.data.id,
        email: response.data.email,
        installation: response.data.installation,
        lastLogin: moment(response.data.last_login),
        created: moment(response.data.created),
        updated: moment(response.data.updated)
      }

      dispatch(userUpdateSuccess(payload))

      return {
        status: response.status,
        message: response.statusText,
      }
    } catch (e) {
      dispatch(userUpdateError([ e.message ]))
      const error = e as AxiosError
      return {
        status: error.code ? parseInt(error.code, 10) : 500,
        message: error.message
      }
    }
  }
}

export {
  fetchUsers,
  updateUser,
  createUser,
  usersSuccess,
  usersError,
  usersRequest,
  userUpdateSuccess,
  userUpdateError,
  userUpdateRequest,
}
